import React, { useState, useEffect } from "react";
import {
  useGetNotificationsQuery,
  useUpdateNotificationMutation,
  useAcknowledgeMutation,
  useDeleteNotificationMutation,
} from "../../../services/groupNotificationAPI";
import { useParams, useNavigate } from "react-router-dom";
import { Table } from "../../../utils/Table";
import { IoMdSettings } from "react-icons/io";
import { ColumnDef } from "@tanstack/react-table";
import { NotificationState } from "../../../features/notifications/notificationSlice";
import { GetGroupUserInfo } from "../../../utils/GroupNotificationUtils";
import ToggleSwitch from "../../../utils/ToggleSwitch";
import Prompt from "../../../utils/Prompt";
import { LanguageText } from "../../../dictionary/GroupNotificationText";
import useGetLanguage from "../../../utils/useGetLanguage";
import { selectUser } from "../../../features/user/userSlice";
import { useAppSelector } from "../../../app/hooks";
import { UserPermissions } from "../../../features/groups/groupSlice";
import { selectGroups } from "../../../features/groups/groupSlice";
import { toast } from "react-toastify";
import { Status, LoadingSpinner, ErrorDisplay } from "../../../utils/StateDisplay";
import Skeleton from "react-loading-skeleton";

const GroupNotificationTable = (): JSX.Element => {
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupNotificationText",
  });
  const [
    updateNotification,
    { isError, error: updateNotificationError, data, isSuccess: isUpdateSuccess },
  ] = useUpdateNotificationMutation();

  const [
    acknowledge,
    {
      isError: isAcknowledgeError,
      error: acknowledgeError,
      data: acknowledgeData,
      isSuccess: isAcknowledgeSuccess,
    },
  ] = useAcknowledgeMutation();

  let navigate = useNavigate();
  const { groupid } = useParams<{ groupid: string }>();
  const userGroupPermissions: UserPermissions =
    useAppSelector(selectUser)?.groups[`${groupid}`]?.permissions;
  const maxNotifications: any =
    useAppSelector(selectGroups)?.groups[`${groupid}`]?.subscription?.perks?.devices;

  const handleToggleSwitchChange = async (notification: any) => {
    const updatedNotification: NotificationState = {
      ...notification,
      status: (({ current, ...rest }) => ({
        ...rest,
        active: !notification.status.active,
      }))(notification.status),
    };
    updatedNotification &&
      (await updateNotification({
        notification: updatedNotification,
        language: Language,
      }));
  };
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [isDelete, setIsDelete] = useState<boolean | null>(null);
  const [showAcknowledgePrompt, setShowAcknowledgePrompt] = useState<boolean>(false);
  const [isAcknowledge, setIsAcknowledge] = useState<boolean | null>(null);

  const handleCloseDeletePrompt = (isDelete: boolean) => {
    setIsDelete(isDelete);
    setShowDeletePrompt(false);
  };

  const handleCloseAcknowledgePrompt = (isAcknowledge: boolean) => {
    setIsAcknowledge(isAcknowledge);
    setShowAcknowledgePrompt(false);
  };

  const handleDeleBtnClick = (id: string) => {
    setShowDeletePrompt(true);
    id !== "" && setDeleteNotificationId(id);
  };

  const handleAcknowledgeBtnClick = (notificationId: string) => {
    setShowAcknowledgePrompt(true);
    notificationId && setAcknowledgeNotificationId(notificationId);
  };

  const {
    data: groupNotificationData,
    isLoading,
    error,
  } = useGetNotificationsQuery(groupid, {
    skip: !userGroupPermissions?.can_view_notifications,
    pollingInterval: 1000 * 10,
  });

  const handleAddBtnClick = () => {
    if (groupNotificationData?.length >= maxNotifications) {
      toast.error(Language?.maxNotificationReached);
      return;
    }
    navigate(`/group/${groupid}/notifications/create`);
  };

  const [deleteNotificationId, setDeleteNotificationId] = useState<string>("");
  const [acknowledgeNotificationId, setAcknowledgeNotificationId] = useState<string>("");

  const [
    deleteNotification,
    {
      isLoading: isDeleteNotificationLoading,
      isError: isDeleteNotificationError,
      error: deleteNotificationError,
    },
  ] = useDeleteNotificationMutation();

  const columns: ColumnDef<NotificationState>[] = [
    {
      accessorFn: (row) => (row.name ? row.name : "No name set"),
      id: "Notification Name",
      cell: ({ row, getValue }) => {
        const notificationid: any = row.original.id;
        return (
          <div className="flex items-center">
            {userGroupPermissions?.can_edit_notifications && (
              <IoMdSettings
                title="Title"
                size={16}
                className="flex-shrink-0 cursor-pointer hover:fill-black"
                onClick={() =>
                  navigate(`/group/${groupid}/notifications/${notificationid}/update`)
                }
              />
            )}
            <div className="ml-2 truncate">{getValue() as string}</div>
          </div>
        );
      },
      header: Language?.name,
    },
    {
      accessorFn: (row) => row?.users ?? [],
      id: "Recipient",
      cell: ({ row, getValue }) => {
        const rowValue = getValue();
        const value = Array.isArray(rowValue) ? rowValue : [];

        // For empty array
        if (value.length === 0) {
          return (
            <div className="flex">
              <div className="ml-2 text-red-600">{`No recipients`}</div>
            </div>
          );
        }
        return (
          <div className="flex flex-wrap">
            {value.map((userid, index) => {
              const { data, isLoading, error } = GetGroupUserInfo({
                groupid: groupid || "",
                userid: userid,
              });
              return (
                <div key={userid} className="ml-2">
                  {isLoading ? (
                    <Skeleton width={100} height={27} />
                  ) : error ? (
                    <span className="text-red-500">Error</span>
                  ) : (
                    <span>
                      {data ? data.email : "Unknown"}
                      {index < value.length - 1 ? "," : ""}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
      header: Language?.recipients,
    },
    {
      accessorFn: (row) => row,
      id: "Enable",
      cell: ({ row, getValue }) => {
        const notification: any = getValue();
        return userGroupPermissions?.can_edit_notifications ? (
          <ToggleSwitch
            isActive={notification?.status?.active}
            onToggle={() => handleToggleSwitchChange(notification)}
          />
        ) : (
          <Status isActive={notification?.status?.active} />
        );
      },
      header: Language?.active,
    },
    {
      accessorFn: (row) => row,
      id: "status",
      cell: ({ row }) => {
        const notification: NotificationState = row?.original;
        const getStatusInfo = (notification: NotificationState) => {
          if (!notification?.status?.current) {
            return {
              isActive: false,
              ts: "",
              tsLabel: undefined,
            };
          }
          const isLatchOn = notification?.configuration?.latch_on;
          return {
            isActive: notification?.status?.current ?? false,
            ts: isLatchOn
              ? notification?.status?.triggered_at
              : notification?.status?.evaluated_at,
            tsLabel: "Triggered at",
          };
        };
        const { isActive, ts, tsLabel } = getStatusInfo(notification);
        return <Status isActive={isActive} ts={ts} tsLabel={tsLabel} />;
      },
      header: "Status",
    },
    {
      accessorFn: (row) => row.id,
      id: "acknowledge",
      cell: ({ row, getValue }) => {
        const notification: any = row.original;
        return (
          <div className="w-fit ml-2">
            {userGroupPermissions?.can_edit_notifications &&
              notification?.configuration?.latch_on &&
              notification?.status?.triggered && (
                <button
                  className={`bg-blue-500 text-white font-bold py-2 px-4  mt-5 mb-0 ml-5 rounded-md shadow-lg hover:bg-blue-700 transition duration-300 ease-in-out transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
                  onClick={() => handleAcknowledgeBtnClick(notification.id)}
                >
                  {Language?.acknowledge}
                </button>
              )}
          </div>
        );
      },
      header: Language?.acknowledge,
    },
    {
      accessorFn: (row) => row.id,
      id: "delete",
      cell: ({ row, getValue }) => {
        const id: any = row.original.id;
        return (
          <div className="w-fit ml-2">
            {userGroupPermissions?.can_edit_notifications && (
              <button
                className={`bg-red-700 text-white font-bold py-2 px-4 rounded-md
              transition-colors duration-200 shadow-sm 
              hover:bg-red-700 focus:bg-red-800 focus:outline-non`}
                onClick={() => handleDeleBtnClick(id)}
              >
                {Language?.delete}
              </button>
            )}
          </div>
        );
      },
      header: Language?.delete,
    },
  ];

  const myTable = Table(
    groupNotificationData,
    columns,
    Language?.notifications,
    "No Notifications to display at the moment"
  );

  useEffect(() => {
    if (isDelete) {
      deleteNotification({ notificationID: deleteNotificationId, language: Language });
      setIsDelete(false);
    }
  }, [isDelete]);

  useEffect(() => {
    if (isAcknowledge) {
      acknowledge({ notificationId: acknowledgeNotificationId, language: Language });
      setIsAcknowledge(false);
    }
  }, [isAcknowledge]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay />;
  }

  return (
    <>
      {showDeletePrompt && (
        <Prompt
          onClose={handleCloseDeletePrompt}
          title={Language?.confirmMessage}
          confirmText={Language?.delete}
          confirmBtnColor="red"
          cancelText={Language?.cancel}
        />
      )}
      {showAcknowledgePrompt && (
        <Prompt
          onClose={handleCloseAcknowledgePrompt}
          title={Language?.confirmMessage}
          confirmText={Language?.acknowledge}
          confirmBtnColor="blue"
          cancelText={Language?.cancel}
        />
      )}
      {userGroupPermissions?.can_edit_notifications && (
        <button
          className="bg-blue-500 text-white font-bold py-2 px-4  mt-5 mb-0 ml-5 rounded-md shadow-lg hover:bg-blue-700 transition duration-300 ease-in-out transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          onClick={handleAddBtnClick}
        >
          {Language?.add}
        </button>
      )}

      {myTable}
    </>
  );
};

export default GroupNotificationTable;
